import { useState, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../../api'
import { cn } from '../../lib/utils'
import type { GraphFilters, GraphNode, GraphEdge, GraphResponse } from './graphTypes'
import { ALL_NODE_TYPES } from './graphTypes'
import { GraphStatsBar } from './GraphStatsBar'
import { GraphControls } from './GraphControls'
import { ForceCanvas } from './ForceCanvas'
import { NodeDetailPanel } from './NodeDetailPanel'

export function KnowledgeGraph() {
  const [filters, setFilters] = useState<GraphFilters>({
    framework: 'ALL',
    nodeTypes: new Set(ALL_NODE_TYPES as unknown as string[]),
    searchQuery: '',
  })
  const [selectedNode, setSelectedNode] = useState<string | null>(null)

  // Fetch graph data
  const { data: graphData, isLoading } = useQuery<GraphResponse>({
    queryKey: ['knowledge-graph', filters.framework],
    queryFn: () => api.get('/knowledge-engine/graph', {
      params: { framework: filters.framework !== 'ALL' ? filters.framework : undefined },
    }).then(r => r.data),
    staleTime: 5 * 60_000,
  })

  // Filter nodes and edges by type + search
  const { filteredNodes, filteredEdges } = useMemo(() => {
    if (!graphData) return { filteredNodes: [] as GraphNode[], filteredEdges: [] as GraphEdge[] }

    const nodes = graphData.nodes.filter(n => filters.nodeTypes.has(n.type))
    const nodeIds = new Set(nodes.map(n => n.id))
    const edges = graphData.edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target))

    return { filteredNodes: nodes, filteredEdges: edges }
  }, [graphData, filters.nodeTypes])

  const handleNavigateToNode = useCallback((targetNodeId: string) => {
    setSelectedNode(targetNodeId)
  }, [])

  if (isLoading) {
    return (
      <div className="flex flex-col h-[calc(100vh-64px)]">
        <div className={cn(
          'flex items-center justify-center flex-1',
          'bg-[var(--bg-base,var(--bg-base))]',
        )}>
          <div className="text-center">
            <div className="h-8 w-8 mx-auto mb-3 rounded-full border-2 border-[var(--accent,var(--accent))] border-t-transparent animate-spin" />
            <p className="text-[13px] text-[var(--text-muted,#64748B)]">
              Loading Knowledge Graph...
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      <GraphStatsBar
        stats={graphData?.stats}
        filters={filters}
        onFrameworkChange={fw => setFilters(prev => ({ ...prev, framework: fw }))}
      />
      <GraphControls filters={filters} onChange={setFilters} />

      <div className="flex-1 relative">
        <ForceCanvas
          nodes={filteredNodes}
          edges={filteredEdges}
          selectedNodeId={selectedNode}
          onNodeClick={id => setSelectedNode(prev => prev === id ? null : id)}
          onNodeHover={() => {}}
          onBackgroundClick={() => setSelectedNode(null)}
          searchQuery={filters.searchQuery}
        />

        <NodeDetailPanel
          nodeId={selectedNode}
          onClose={() => setSelectedNode(null)}
          onNavigateToNode={handleNavigateToNode}
        />
      </div>
    </div>
  )
}
